import { AbstractControl } from '@angular/forms';

/**
 * Decides whether a form still holds edits the user would lose.
 *
 * A prompt needs the value to differ from the baseline *and* the form to be dirty, because neither
 * signal is trustworthy alone: child ControlValueAccessors report changes through their change
 * callback as they initialise, which the forms pipeline records as a user edit, and loading a screen
 * legitimately rewrites the value after the baseline was taken — a dropdown swapping a raw id for
 * `{ id, text }` once its options arrive, say.
 *
 * A value that changed while the form is pristine therefore re-arms the baseline. That is checked on
 * read rather than through `valueChanges`, since a programmatic write may pass `{ emitEvent: false }`
 * and never announce itself.
 */
export class UnsavedChangesTracker {
  private baseline: string;
  private outOfFormChange = false;

  constructor(private readonly form: AbstractControl) {
    this.baseline = this.serialise();
  }

  /** Call once the form is populated from the backend and after every successful save. */
  reset(): void {
    this.baseline = this.serialise();
    this.outOfFormChange = false;
    this.form.markAsPristine();
  }

  /** Records an edit the form value cannot express — a translation dialog, a non-form control. */
  markChanged(): void {
    this.outOfFormChange = true;
  }

  hasChanges(): boolean {
    if (this.outOfFormChange) {
      return true;
    }

    const current = this.serialise();
    if (current === this.baseline) {
      return false;
    }

    if (this.form.pristine) {
      this.baseline = current;

      return false;
    }

    return true;
  }

  private serialise(): string {
    return JSON.stringify(this.form.getRawValue() ?? null);
  }
}
